import React from 'react';
import cn from 'classnames';

import type { MediaAttachment } from 'widget/types/attachments';
import MediaViewer from 'widget/components/MediaViewer/MediaViewer';
import { isVideo } from 'widget/utils/getAttachmentType';

import MessageImage from '../MessageImage';
import MessageVideo from '../MessageVideo';

import styles from './MessageMediaGrid.module.scss';

const getGridClass = (count: number) => {
  return styles[`grid_${count}`];
};

export type MessageMediaGridProps = {
  media: MediaAttachment[];
  isFromBot?: boolean;
};

const MessageMediaGrid: React.FC<MessageMediaGridProps> = ({
  media,
  isFromBot = false
}: MessageMediaGridProps) => {
  const [viewerIndex, setViewerIndex] = React.useState<number | null>(null);

  const handleOpenViewer = React.useCallback((index: number | null) => {
    setViewerIndex(index);
  }, []);

  const handleCloseViewer = React.useCallback(() => {
    setViewerIndex(null);
  }, []);

  const gridClass = React.useMemo(() => {
    return getGridClass(media.length);
  }, [media.length]);

  if (media.length === 0) {
    return null;
  }

  return (
    <div className={cn(styles['grid'], gridClass)}>
      {media.map((mediaAttachment, index) =>
        isVideo(mediaAttachment.type) ? (
          <MessageVideo
            key={`${mediaAttachment.url}-${index}`}
            url={mediaAttachment.url}
            filename={mediaAttachment.filename}
            size={mediaAttachment.size}
            width={mediaAttachment.width}
            height={mediaAttachment.height}
            previewUrl={mediaAttachment.preview_url}
            isFromBot={isFromBot}
            onClick={() => handleOpenViewer(index)}
          />
        ) : (
          <MessageImage
            key={`${mediaAttachment.url}-${index}`}
            url={mediaAttachment.url}
            filename={mediaAttachment.filename}
            size={mediaAttachment.size}
            height={mediaAttachment.height}
            width={mediaAttachment.width}
            isFromBot={isFromBot}
            onClick={() => handleOpenViewer(index)}
          />
        )
      )}
      {viewerIndex !== null && (
        <MediaViewer
          media={media}
          initialIndex={viewerIndex}
          onClose={handleCloseViewer}
        />
      )}
    </div>
  );
};

export default React.memo(MessageMediaGrid);
